<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import DocumentSignDetails from '../components/js/Components/Document/DocumentSignDetails.vue'
import DocumentSignList from '../components/js/Components/Document/DocumentSignList.vue'
import ButtonAll from '../components/js/Components/UI/Button/ButtonAll.vue'
import BlockContent from '../components/js/Components/UI/Content/BlockContent.vue'

import { useDocumentSignApi } from '../Composables/DocumentSign/useDocumentSignApi.ts'
import useLoading from '../Composables/useLoading.ts'
import { useQueryParameter } from '../Composables/useQueryParameter.ts'
import type { Contract } from '../Types/contract'
import type { DocumentSign } from '../Types/document'
import ModalConfirmation from '../components/js/Components/UI/Modal/ModalConfirmation.vue'
import useModal from '../Composables/useModal.ts'

const props = defineProps<{
  contract: Contract
}>()

const { create, fetchContractDocumentSign, createContractDocumentSign } = useDocumentSignApi()
const { loading } = useLoading(false)
const { loading: creatingContractDocumentSign } = useLoading(false)
const { loading: fetchingContractDocumentSign } = useLoading(false)
const { value: openedDocumentSignId } = useQueryParameter('documentSignId')

const showDetail = computed<boolean>(
  () => openedDocumentSignId.value !== null && !isNaN(Number(openedDocumentSignId.value)),
)

function setOpenedDocumentSignId(value: number | null): void {
  openedDocumentSignId.value = value
  if (value === null) {
    fetchContractFlow()
  }
}

const contractDocumentSign = ref<DocumentSign | null>(null)
async function fetchContractFlow(): Promise<void> {
  fetchingContractDocumentSign.value = true
  try {
    contractDocumentSign.value = await fetchContractDocumentSign({ contractId: props.contract.id })
  } finally {
    fetchingContractDocumentSign.value = false
  }
}

async function createContractFlow(): Promise<void> {
  creatingContractDocumentSign.value = true
  try {
    const documentSign = await createContractDocumentSign({ contractId: props.contract.id })
    if (documentSign) {
      setOpenedDocumentSignId(documentSign.id)
    }
  } finally {
    creatingContractDocumentSign.value = false
  }
}

async function createBlankSignature(): Promise<void> {
  loading.value = true
  try {
    const { id } = await create({ contractId: props.contract.id })

    setOpenedDocumentSignId(id)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchContractFlow()
})
</script>

<template>
  <div v-if="!showDetail">
    <BlockContent>
      <div v-if="!fetchingContractDocumentSign" class="mb-3 d-flex justify-content-end">
        <ButtonAll
          v-if="!contractDocumentSign"
          margin="no"
          type="action"
          class="mr-2"
          label="contract.document.documentSign.createContractDocumentSignButton"
          :loading="creatingContractDocumentSign"
          @click="createContractFlow"
        />
        <ButtonAll
          margin="no"
          type="action"
          label="contract.document.documentSign.createEmptyDocumentSignButton"
          :loading="loading"
          @click="createBlankSignature"
        />
      </div>

      <DocumentSignList
        ref="documentSignerList"
        :contract-id="contract.id"
        @open-document-sign="setOpenedDocumentSignId($event)"
      />
    </BlockContent>
  </div>
  <div v-else>
    <DocumentSignDetails
      :contract-id="contract.id"
      :document-sign-id="Number(openedDocumentSignId)"
      @close="setOpenedDocumentSignId(null)"
    />
  </div>
</template>
